import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Button,
  Select,
} from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";

// ─── Score type options ───────────────────────────────────────────────────────

const SCORE_TYPES = [
  { label: "RED Score",   value: 1 },
  { label: "SOVI Report", value: 2 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const nullToZero = (val) => (val === null || val === undefined ? 0 : Number(val));

const buildShelfColumns = (maxShelves) => {
  const cols = [];
  for (let i = 1; i <= maxShelves; i++) {
    cols.push({
      headerName: `SHELF ${i} (%)`,
      field: `shelf${i}Pur`,
      minWidth: 120,
      flex: 1,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        color: "#2D3748",
      },
      valueFormatter: (params) =>
        params.value !== undefined ? `${params.value}%` : "0%",
    });
  }
  return cols;
};

// ─── AG Grid styling ──────────────────────────────────────────────────────────

const gridSx = {
  "& .ag-header": { backgroundColor: "#C72127", borderBottom: "none" },
  "& .ag-header-cell": {
    backgroundColor: "#C72127",
    color: "white",
    fontWeight: "bold",
    borderRight: "1px solid rgba(255,255,255,0.2)",
  },
  "& .ag-header-cell-text": { color: "white", fontSize: "14px", fontWeight: "bold" },
  "& .ag-header-icon": { color: "white" },
  "& .ag-cell": {
    fontSize: "14px",
    padding: "8px 12px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    borderRight: "2px solid #cbd5e0",
    borderBottom: "2px solid #cbd5e0",
  },
  "& .ag-paging-panel": { display: "none" },
  "& .ag-row": { borderBottom: "1px solid #cbd5e0" },
  "& .ag-root-wrapper": { border: "1px solid #cbd5e0" },
  "& .ag-filter": {
    backgroundColor: "white",
    padding: "12px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  "& .ag-filter input": {
    padding: "8px 12px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
  },
  "& .ag-filter select": {
    padding: "8px 12px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
    backgroundColor: "white",
  },
  "& .ag-filter button": {
    padding: "6px 12px",
    fontSize: "13px",
    borderRadius: "6px",
    marginTop: "8px",
  },
};

// ─── Static base column definitions ──────────────────────────────────────────

const BASE_COLUMN_DEFS = [
  { headerName: "OUTLET CODE",      field: "outletCode",      minWidth: 140, flex: 1 },
  { headerName: "STORE NAME",       field: "storeName",       minWidth: 200, flex: 1.8 },
  { headerName: "STORE TYPE",       field: "storeType",       minWidth: 140, flex: 1 },
  { headerName: "STORE CLASS",      field: "storeCls",        minWidth: 130, flex: 1 },
  { headerName: "TOTAL SHELVES",    field: "totalNoOfShelves", minWidth: 130, flex: 1,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      color: "#2D3748",
    },
  },
];

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  suppressMovable: true,
  filterParams: { maxNumConditions: 1, buttons: ["reset"] },
  suppressCellFocus: true,
  cellStyle: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "normal",
    lineHeight: "1.5",
    fontSize: "14px",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

function CoolerImpurityDp({ fromDate, toDate }) {
  const [scoreTypeId,   setScoreTypeId]   = useState(1);
  const [rowData,       setRowData]       = useState([]);
  const [columnDefs,    setColumnDefs]    = useState(BASE_COLUMN_DEFS);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [gridApi,       setGridApi]       = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [currentPage,   setCurrentPage]   = useState(1);
  const pageSize = 20;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    setRowData([]);

    const [fromYear, fromMonth, fromDay] = fromDate.split("-").map(Number);
    const [toYear,   toMonth,   toDay]   = toDate.split("-").map(Number);

    const requestBody = {
      from: { date: fromDay,  month: fromMonth, year: fromYear },
      to:   { date: toDay,    month: toMonth,   year: toYear   },
    };

    try {
      const res  = await apiClient.post(
        `${API_CONFIG.BASE_URL}/api/stores/score/${scoreTypeId}`,
        requestBody
      );
      const list = Array.isArray(res.data) ? res.data : [res.data];

      // Determine the max number of shelves across all records
      const maxShelves = list.reduce(
        (max, item) => Math.max(max, Number(item?.totalNoOfShelves ?? 0)),
        0
      );

      // Map rows — replace null shelf values with 0
      const mapped = list.map((item) => {
        const row = {
          outletCode:      item?.outletCode      ?? "",
          storeName:       item?.storeName       ?? "",
          storeType:       item?.storeType       ?? "",
          storeCls:        item?.storeCls        ?? "",
          totalNoOfShelves: nullToZero(item?.totalNoOfShelves),
        };

        for (let i = 1; i <= 10; i++) {
          const key = `shelf${i}Pur`;
          row[key] = nullToZero(item?.[key]);
        }

        return row;
      });

      // Build shelf columns only up to the max shelves in this data set
      setColumnDefs([...BASE_COLUMN_DEFS, ...buildShelfColumns(maxShelves)]);
      setRowData(mapped);
    } catch (err) {
      console.error("Failed to fetch SOVI/RED data:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, scoreTypeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Grid callbacks ─────────────────────────────────────────────────────────

  const onGridReady = (params) => {
    setGridApi(params.api);
    setFilteredCount(params.api.getDisplayedRowCount());
  };

  const onFilterChanged = useCallback((params) => {
    if (params?.api) setFilteredCount(params.api.getDisplayedRowCount());
  }, []);

  const totalPages = Math.ceil(filteredCount / pageSize);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
      gridApi?.paginationGoToPreviousPage();
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
      gridApi?.paginationGoToNextPage();
    }
  };

  // ── Score type change — reset page ─────────────────────────────────────────

  const handleScoreTypeChange = (e) => {
    setScoreTypeId(Number(e.target.value));
    setCurrentPage(1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Toolbar: score-type selector + record count */}
      <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        {/* Score type selector */}
        <Select
          value={scoreTypeId}
          onChange={handleScoreTypeChange}
          size="sm"
          width="180px"
          borderRadius="lg"
          focusBorderColor="red.500"
          bg="white"
          borderColor="gray.300"
          fontWeight="semibold"
        >
          {SCORE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>

        {/* Record count pill */}
        <Flex
          align="center"
          gap={1.5}
          bg="gray.700"
          px={4}
          py={1.5}
          borderRadius="full"
          boxShadow="0 2px 4px rgba(0,0,0,0.2)"
        >
          <Text fontSize="sm" fontWeight="bold" color="white">
            {filteredCount < rowData.length ? "Filtered:" : "Total:"}
          </Text>
          <Text color="white" fontSize="md" fontWeight="bold">
            {filteredCount} Records
          </Text>
        </Flex>
      </Flex>

      {error && (
        <Text color="red.500" fontWeight="bold" mb={3}>{error}</Text>
      )}

      {/* AG Grid */}
      <Box
        className="ag-theme-alpine"
        h={{ base: "300px", sm: "400px", md: "500px", lg: "600px" }}
        w="100%"
        bg="white"
        borderRadius="8px"
        boxShadow="0 2px 8px rgba(0,0,0,0.1)"
        mb={3}
        overflow="hidden"
        sx={gridSx}
      >
        {loading ? (
          <Flex justify="center" align="center" h="100%">
            <Spinner size="xl" color="red.500" thickness="6px" />
          </Flex>
        ) : (
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination
            paginationPageSize={pageSize}
            domLayout="normal"
            suppressCellFocus
            onFilterChanged={onFilterChanged}
            animateRows
            rowHeight={60}
            headerHeight={65}
            suppressPaginationPanel
            onGridReady={onGridReady}
            onPaginationChanged={(params) =>
              setCurrentPage(params.api.paginationGetCurrentPage() + 1)
            }
            enableCellTextSelection
            ensureDomOrder
            overlayNoRowsTemplate={`
              <div style="padding:40px;text-align:center;">
                <div style="font-size:18px;font-weight:bold;color:#2D3748;margin-bottom:8px;">No Data Available</div>
                <div style="font-size:14px;color:#718096;">No records found for the selected date range</div>
              </div>
            `}
          />
        )}
      </Box>

      {/* Custom Pagination */}
      <Flex justify="space-between" align="center" mt={4} px={2}>
        <Text fontSize="sm" color="gray.600">
          Showing {filteredCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
          {Math.min(currentPage * pageSize, filteredCount)} of {filteredCount} records
        </Text>

        <Flex gap={2} align="center">
          <Button
            onClick={handlePrevious}
            isDisabled={currentPage === 1}
            size="sm"
            variant="outline"
            colorScheme="gray"
            leftIcon={<ChevronLeftIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Previous
          </Button>

          <Flex
            align="center"
            justify="center"
            bg="red.500"
            color="white"
            w="40px"
            h="40px"
            borderRadius="md"
            fontWeight="bold"
            fontSize="md"
            boxShadow="0 2px 4px rgba(199,33,39,0.3)"
          >
            {currentPage}
          </Flex>

          <Button
            onClick={handleNext}
            isDisabled={currentPage >= totalPages}
            size="sm"
            variant="outline"
            colorScheme="gray"
            rightIcon={<ChevronRightIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}

export default CoolerImpurityDp;